/**
 * Pure TEA update for converting a project to `.zwdoc` on open (#259).
 *
 * Shell: `useProjectConversionStore.ts` (list legacy documents, plan, apply,
 * reload). The model drives the blocking notice while converting and the
 * failure dialog when a document can't be converted safely.
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";
import type { ConversionFailure } from "../lib/zwdoc/convertProject";

export type ProjectConversionModel =
  | { readonly phase: "idle" }
  | { readonly phase: "converting"; readonly documents: number }
  | {
      readonly phase: "failed";
      /** Per-document reasons; empty when the backend write itself failed. */
      readonly failures: readonly ConversionFailure[];
      readonly error: string;
    };

export const initialProjectConversionModel: ProjectConversionModel = {
  phase: "idle",
};

export type ProjectConversionMsg =
  | { readonly type: "Started"; readonly documents: number }
  | { readonly type: "Converted" }
  | {
      readonly type: "PlanRejected";
      readonly failures: readonly ConversionFailure[];
    }
  | { readonly type: "ApplyFailed"; readonly error: string }
  | { readonly type: "Dismissed" };

export function updateProjectConversion(
  _model: ProjectConversionModel,
  msg: ProjectConversionMsg,
): readonly [ProjectConversionModel, readonly never[]] {
  switch (msg.type) {
    case "Started":
      return [{ phase: "converting", documents: msg.documents }, []];
    case "Converted":
    case "Dismissed":
      return [initialProjectConversionModel, []];
    case "PlanRejected":
      return [
        {
          phase: "failed",
          failures: msg.failures,
          error: `${String(msg.failures.length)} document${msg.failures.length === 1 ? "" : "s"} couldn't be converted.`,
        },
        [],
      ];
    case "ApplyFailed":
      return [{ phase: "failed", failures: [], error: msg.error }, []];
    default:
      return assertNever(msg);
  }
}

export const updateProjectConversionFn: UpdateFn<
  ProjectConversionModel,
  ProjectConversionMsg,
  never
> = updateProjectConversion;
